import { FormEvent, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useSignIn, useSignUp, useUser } from '@clerk/clerk-react';
import Modal from '../ui/Modal';
import Input from '../ui/Input';
import Button from '../ui/Button';
import VerificationCodeInput from '../ui/VerificationCodeInput';
import { useAuthModal } from './AuthModalContext';
import AccountSetupForm, { type AccountSetupFormHandle } from './AccountSetupForm';
import { isAccountSetupCompleted } from '../../types/userProfile';

type OAuthStrategy = 'oauth_google' | 'oauth_microsoft' | 'oauth_linkedin_oidc';
type EmailAuthMethod = 'code' | 'password';

type ClerkLikeResult = {
  status: string | null;
  createdSessionId: string | null;
  missingFields?: string[];
};

function clerkErrorMessage(error: unknown): string {
  if (error && typeof error === 'object' && 'errors' in error) {
    const errors = (error as { errors?: Array<{ longMessage?: string; message?: string }> }).errors;
    const first = errors?.[0];
    if (first?.longMessage) return first.longMessage;
    if (first?.message) return first.message;
  }
  if (error instanceof Error && error.message) return error.message;
  return 'Something went wrong. Please try again.';
}

function clerkErrorCode(error: unknown): string | undefined {
  if (error && typeof error === 'object' && 'errors' in error) {
    return (error as { errors?: Array<{ code?: string }> }).errors?.[0]?.code;
  }
  return undefined;
}

function isPasswordRequiredError(message: string): boolean {
  return /password/i.test(message) && /required|must|enter/i.test(message);
}

function normalizeCode(raw: string): string {
  return raw.replace(/\D/g, '').slice(0, 6);
}

function generateSatisfyingPassword(): string {
  const bytes = new Uint8Array(24);
  crypto.getRandomValues(bytes);
  const raw = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
  return `Aa1!${raw}`;
}

function sameEmail(value: string | null | undefined, email: string): boolean {
  return value?.trim().toLowerCase() === email.trim().toLowerCase();
}

function isSignUpEmailVerified(resource: {
  verifications?: { emailAddress?: { status?: string | null } };
} | null | undefined): boolean {
  return resource?.verifications?.emailAddress?.status === 'verified';
}

export default function AuthModal() {
  const { isOpen, mode, phase, setMode, closeAuthModal, continueAccountSetup } = useAuthModal();
  const { isLoaded: signInLoaded, signIn, setActive: setActiveSignIn } = useSignIn();
  const { isLoaded: signUpLoaded, signUp, setActive: setActiveSignUp } = useSignUp();
  const { user, isLoaded: userLoaded } = useUser();
  const location = useLocation();
  const setupFormRef = useRef<AccountSetupFormHandle>(null);
  const [setupSkipped, setSetupSkipped] = useState(false);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [needsVerification, setNeedsVerification] = useState(false);
  const [emailAuthMethod, setEmailAuthMethod] = useState<EmailAuthMethod>('code');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [oauthError, setOauthError] = useState<string | null>(null);
  const verifyingRef = useRef(false);

  useEffect(() => {
    if (!isOpen) {
      setEmail('');
      setPassword('');
      setFirstName('');
      setLastName('');
      setVerificationCode('');
      setNeedsVerification(false);
      setEmailAuthMethod('code');
      setError(null);
      setOauthError(null);
      setSubmitting(false);
      verifyingRef.current = false;
    }
  }, [isOpen]);

  const loaded = signInLoaded && signUpLoaded;
  const title = phase === 'setup' ? 'Finish setting up your account' : mode === 'sign-in' ? 'Sign in' : 'Create an account';

  useEffect(() => {
    if (!userLoaded || !user) return;
    if (isAccountSetupCompleted(user.unsafeMetadata)) return;
    if (setupSkipped) return;
    if (location.pathname === '/profile' || location.pathname === '/sso-callback') return;
    if (isOpen && phase === 'setup') return;
    continueAccountSetup();
  }, [
    userLoaded,
    user,
    location.pathname,
    isOpen,
    phase,
    setupSkipped,
    continueAccountSetup,
  ]);

  function afterAuthenticated() {
    if (user && isAccountSetupCompleted(user.unsafeMetadata)) {
      closeAuthModal();
      return;
    }
    continueAccountSetup();
  }

  function resetTransientState() {
    setNeedsVerification(false);
    setVerificationCode('');
    setError(null);
    setOauthError(null);
  }

  function switchEmailAuthMethod(next: EmailAuthMethod) {
    setEmailAuthMethod(next);
    resetTransientState();
  }

  async function handleOAuth(strategy: OAuthStrategy) {
    setOauthError(null);
    try {
      const redirect = {
        strategy,
        redirectUrl: '/sso-callback',
        redirectUrlComplete: window.location.pathname || '/',
      };
      if (mode === 'sign-up' && signUp) {
        await signUp.authenticateWithRedirect(redirect);
        return;
      }
      if (!signIn) return;
      await signIn.authenticateWithRedirect(redirect);
    } catch (err) {
      setOauthError(clerkErrorMessage(err));
    }
  }

  async function sendSignInCode(resend = false) {
    if (!signIn) return;
    if (!resend || !signIn.supportedFirstFactors?.length) {
      await signIn.create({ identifier: email.trim() });
    }
    const factor = signIn.supportedFirstFactors?.find((item) => item.strategy === 'email_code');
    if (!factor || factor.strategy !== 'email_code') {
      throw new Error(
        'Email code sign-in is not enabled for this account. Use a password, or turn on email verification codes in Clerk.'
      );
    }
    await signIn.prepareFirstFactor({
      strategy: 'email_code',
      emailAddressId: factor.emailAddressId,
    });
    setNeedsVerification(true);
  }

  async function sendSignUpCode(forcePrepare = false) {
    if (!signUp) return;
    const nextAction = signUp.verifications?.emailAddress?.nextAction;
    if (forcePrepare || nextAction !== 'needs_attempt') {
      await signUp.prepareEmailAddressVerification({ strategy: 'email_code' });
    }
    setNeedsVerification(true);
  }

  async function completeSignUpIfPossible(result?: ClerkLikeResult | null) {
    const status = result?.status ?? signUp?.status ?? null;
    const sessionId = result?.createdSessionId ?? signUp?.createdSessionId ?? null;
    if (status === 'complete' && sessionId && setActiveSignUp) {
      await setActiveSignUp({ session: sessionId });
      afterAuthenticated();
      return true;
    }
    return false;
  }

  async function finishVerifiedSignUp() {
    if (!signUp) return false;
    if (await completeSignUpIfPossible(signUp)) return true;

    const patch: {
      firstName?: string;
      lastName?: string;
      legalAccepted?: boolean;
      password?: string;
    } = {};
    if (signUp.missingFields.includes('first_name') && firstName.trim()) {
      patch.firstName = firstName.trim();
    }
    if (signUp.missingFields.includes('last_name') && lastName.trim()) {
      patch.lastName = lastName.trim();
    }
    if (signUp.missingFields.includes('legal_accepted')) {
      patch.legalAccepted = true;
    }
    if (signUp.missingFields.includes('password')) {
      patch.password = generateSatisfyingPassword();
    }
    if (Object.keys(patch).length > 0) {
      const updated = await signUp.update(patch);
      if (await completeSignUpIfPossible(updated)) return true;
    }

    return completeSignUpIfPossible(signUp);
  }

  async function createSignUp() {
    if (!signUp) {
      throw new Error('Sign up is not ready yet.');
    }
    const requestedEmail = email.trim();
    const first = firstName.trim() || undefined;
    const last = lastName.trim() || undefined;
    const payload =
      emailAuthMethod === 'password'
        ? {
            emailAddress: requestedEmail,
            password,
            firstName: first,
            lastName: last,
          }
        : {
            emailAddress: requestedEmail,
            firstName: first,
            lastName: last,
          };

    try {
      return await signUp.create(payload);
    } catch (err) {
      if (emailAuthMethod === 'code' && isPasswordRequiredError(clerkErrorMessage(err))) {
        return signUp.create({
          emailAddress: requestedEmail,
          firstName: first,
          lastName: last,
          password: generateSatisfyingPassword(),
        });
      }
      throw err;
    }
  }

  async function handleEmailSubmit(event: FormEvent) {
    event.preventDefault();
    if (mode === 'sign-in') {
      await handleSignIn();
      return;
    }
    await handleSignUp();
  }

  async function handleSignIn() {
    if (!signIn || !setActiveSignIn) return;
    setSubmitting(true);
    setError(null);
    try {
      if (emailAuthMethod === 'code') {
        await sendSignInCode();
        return;
      }
      const result = await signIn.create({ identifier: email.trim(), password });
      if (result.status === 'complete' && result.createdSessionId) {
        await setActiveSignIn({ session: result.createdSessionId });
        afterAuthenticated();
        return;
      }
      setError('Additional verification is required. Check your email and try again.');
    } catch (err) {
      setError(clerkErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  }

  async function handleSignUp() {
    if (!signUp || !setActiveSignUp) return;
    setSubmitting(true);
    setError(null);
    try {
      const requestedEmail = email.trim();
      const inProgress =
        signUp.status === 'missing_requirements' && sameEmail(signUp.emailAddress, requestedEmail);

      if (inProgress && isSignUpEmailVerified(signUp)) {
        if (emailAuthMethod === 'password' && password) {
          const updated = await signUp.update({
            password,
            firstName: firstName.trim() || undefined,
            lastName: lastName.trim() || undefined,
          });
          if (await completeSignUpIfPossible(updated)) return;
        }
        if (await finishVerifiedSignUp()) return;
        setError('Your email is verified, but the account could not be finished. Try again.');
        return;
      }

      if (inProgress) {
        await sendSignUpCode();
        return;
      }

      const result = await createSignUp();
      if (await completeSignUpIfPossible(result)) return;
      if (isSignUpEmailVerified(result) || isSignUpEmailVerified(signUp)) {
        if (await finishVerifiedSignUp()) return;
      }
      await sendSignUpCode();
    } catch (err) {
      const message = clerkErrorMessage(err);
      if (clerkErrorCode(err) === 'form_identifier_exists') {
        setMode('sign-in');
        setEmailAuthMethod('code');
        try {
          await sendSignInCode();
          setError('An account with that email already exists. We sent a sign-in code instead.');
        } catch (signInErr) {
          setError(clerkErrorMessage(signInErr));
        }
        return;
      }
      setError(message);
    } finally {
      setSubmitting(false);
    }
  }

  async function handleVerify(event?: FormEvent, code = verificationCode) {
    event?.preventDefault();
    const trimmed = normalizeCode(code);
    if (trimmed.length !== 6 || verifyingRef.current) return;
    verifyingRef.current = true;
    setSubmitting(true);
    setError(null);
    try {
      if (mode === 'sign-in') {
        if (!signIn || !setActiveSignIn) return;
        const result = await signIn.attemptFirstFactor({
          strategy: 'email_code',
          code: trimmed,
        });
        if (result.status === 'complete' && result.createdSessionId) {
          await setActiveSignIn({ session: result.createdSessionId });
          afterAuthenticated();
          return;
        }
        setError('Could not verify that code. Try again or request a new one.');
        return;
      }

      if (!signUp || !setActiveSignUp) return;
      const result = await signUp.attemptEmailAddressVerification({ code: trimmed });
      if (await completeSignUpIfPossible(result)) return;
      if (isSignUpEmailVerified(result) || isSignUpEmailVerified(signUp) || result.status === 'missing_requirements') {
        if (await finishVerifiedSignUp()) return;
        if (isSignUpEmailVerified(signUp)) {
          const leftover = (signUp.missingFields || []).join(', ');
          setError(
            leftover
              ? `Your email is verified, but this is still required: ${leftover}.`
              : 'Your email is verified, but the account could not be finished. Try again.'
          );
          return;
        }
      }
      setError('Could not verify that code. Try again or request a new one.');
    } catch (err) {
      setError(clerkErrorMessage(err));
    } finally {
      verifyingRef.current = false;
      setSubmitting(false);
    }
  }

  async function handleResendCode() {
    if (verifyingRef.current) return;
    verifyingRef.current = true;
    setSubmitting(true);
    setError(null);
    try {
      if (mode === 'sign-in') {
        await sendSignInCode(true);
      } else {
        await sendSignUpCode(true);
      }
      setVerificationCode('');
    } catch (err) {
      setError(clerkErrorMessage(err));
    } finally {
      verifyingRef.current = false;
      setSubmitting(false);
    }
  }

  const primaryLabel =
    emailAuthMethod === 'code' ? 'Email me a code' : mode === 'sign-in' ? 'Sign in' : 'Create account';

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        if (phase === 'setup') {
          if (setupFormRef.current) {
            setupFormRef.current.dismiss();
            return;
          }
        }
        closeAuthModal();
      }}
      title={title}
      size={phase === 'setup' ? 'lg' : 'sm'}
    >
      {phase === 'setup' ? (
        <AccountSetupForm
          ref={setupFormRef}
          onFinished={() => {
            setSetupSkipped(true);
            closeAuthModal();
          }}
        />
      ) : (
        <>
      {oauthError && (
        <p className="mb-4 text-sm text-error" role="alert">
          {oauthError}
        </p>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
        <Button type="button" variant="secondary" className="w-full min-h-[44px] px-3" onClick={() => handleOAuth('oauth_google')}>
          Google
        </Button>
        <Button type="button" variant="secondary" className="w-full min-h-[44px] px-3" onClick={() => handleOAuth('oauth_microsoft')}>
          Microsoft
        </Button>
        <Button
          type="button"
          variant="secondary"
          className="w-full min-h-[44px] px-3"
          onClick={() => handleOAuth('oauth_linkedin_oidc')}
        >
          LinkedIn
        </Button>
      </div>

      <div className="relative mb-6">
        <div className="absolute inset-0 flex items-center" aria-hidden="true">
          <div className="w-full border-t border-gray-200" />
        </div>
        <div className="relative flex justify-center">
          <span className="bg-white px-2 text-sm text-gray-500">or continue with email</span>
        </div>
      </div>

      {needsVerification ? (
        <form
          onSubmit={(event) => {
            event.preventDefault();
            void handleVerify(event, verificationCode);
          }}
          className="space-y-4"
        >
          <p className="text-sm text-gray-600">
            Enter the verification code we sent to {email}.
          </p>
          <VerificationCodeInput
            value={verificationCode}
            onChange={setVerificationCode}
            onComplete={(code) => void handleVerify(undefined, code)}
            error={error ?? undefined}
            disabled={submitting}
          />
          <Button
            type="submit"
            className="w-full min-h-[44px]"
            disabled={!loaded || submitting || normalizeCode(verificationCode).length !== 6}
          >
            {submitting ? 'Please wait…' : 'Verify code'}
          </Button>
          <button
            type="button"
            className="w-full text-sm text-primary font-medium hover:text-primary-dark focus-ring rounded-sm min-h-[44px]"
            disabled={submitting}
            onClick={() => void handleResendCode()}
          >
            Resend code
          </button>
          <button
            type="button"
            className="w-full text-sm text-primary font-medium hover:text-primary-dark focus-ring rounded-sm min-h-[44px]"
            onClick={() => {
              resetTransientState();
            }}
          >
            Use a different email
          </button>
        </form>
      ) : (
        <form onSubmit={handleEmailSubmit} className="space-y-4">
          {mode === 'sign-up' && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Input
                label="First name"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                autoComplete="given-name"
                required
              />
              <Input
                label="Last name"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                autoComplete="family-name"
                required
              />
            </div>
          )}
          <Input
            label="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
            required
          />
          {emailAuthMethod === 'password' && (
            <Input
              label="Password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete={mode === 'sign-in' ? 'current-password' : 'new-password'}
              required
            />
          )}
          {error && (
            <p className="text-sm text-error" role="alert">
              {error}
            </p>
          )}
          <Button type="submit" className="w-full min-h-[44px]" disabled={!loaded || submitting}>
            {submitting ? 'Please wait…' : primaryLabel}
          </Button>
          <button
            type="button"
            className="w-full text-sm text-gray-600 hover:text-primary focus-ring rounded-sm min-h-[44px]"
            onClick={() => switchEmailAuthMethod(emailAuthMethod === 'code' ? 'password' : 'code')}
          >
            {emailAuthMethod === 'code' ? 'Use a password instead' : 'Email me a code instead'}
          </button>
        </form>
      )}

      <p className="mt-6 text-sm text-gray-600 text-center">
        {mode === 'sign-in' ? (
          <>
            New here?{' '}
            <button
              type="button"
              className="text-primary font-medium hover:text-primary-dark focus-ring rounded-sm"
              onClick={() => {
                resetTransientState();
                setMode('sign-up');
              }}
            >
              Create an account
            </button>
          </>
        ) : (
          <>
            Already have an account?{' '}
            <button
              type="button"
              className="text-primary font-medium hover:text-primary-dark focus-ring rounded-sm"
              onClick={() => {
                resetTransientState();
                setMode('sign-in');
              }}
            >
              Sign in
            </button>
          </>
        )}
      </p>
        </>
      )}
    </Modal>
  );
}
